// LookupTablesTab.js - Simple key-value lookups (1D) like ref_Textured, ref_brickmoldSize
import {
  React,
  useEffect,
  useState,
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  DataGrid,
  authFetch,
  authFetchRequest,
  alertC,
  confirmC
} from './PricingCommon';

const LookupTablesTab = () => {
  const [lookups, setLookups] = useState([]);
  const [entries, setEntries] = useState([]);
  const [selectedLookup, setSelectedLookup] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [openLookupDialog, setOpenLookupDialog] = useState(false);
  const [openEntryDialog, setOpenEntryDialog] = useState(false);
  const [editLookupData, setEditLookupData] = useState(null);
  const [editEntryData, setEditEntryData] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });

  // Fetch all lookup tables
  const fetchLookups = () => {
    setLoading(true);
    authFetch('/pricing/lookups')
      .then(data => { setLookups(data); setLoading(false); })
      .catch(err => { console.error('Failed to fetch lookups:', err); setLoading(false); });
  };

  // Fetch entries for selected lookup
  const fetchEntries = (lookupId) => {
    if (!lookupId) {
      setEntries([]);
      return;
    }
    setLoadingEntries(true);
    authFetch(`/pricing/lookups/${lookupId}/entries`)
      .then(data => { setEntries(data); setLoadingEntries(false); })
      .catch(err => { console.error('Failed to fetch entries:', err); setLoadingEntries(false); });
  };

  useEffect(() => { fetchLookups(); }, []);

  useEffect(() => {
    if (selectedLookup) {
      fetchEntries(selectedLookup.id);
    } else {
      setEntries([]);
    }
  }, [selectedLookup]);

  // Lookup table CRUD
  const handleSaveLookup = async (formData) => {
    try {
      let updatedRow;
      if (editLookupData?.id) {
        updatedRow = await authFetchRequest(`/pricing/lookups/${editLookupData.id}`, formData, 'PUT');
        setLookups(prev => prev.map(row => row.id === editLookupData.id ? updatedRow : row));
        if (selectedLookup?.id === editLookupData.id) setSelectedLookup(updatedRow);
      } else {
        updatedRow = await authFetchRequest('/pricing/lookups', formData, 'POST');
        setLookups(prev => [...prev, updatedRow]);
      }
      setOpenLookupDialog(false);
      setEditLookupData(null);

    } catch (err) {
      console.error('Save failed:', err);
      alertC('Failed to save lookup table');
    }
  };

  const handleDeleteLookup = async () => {
    if (!selectedLookup) return;
    const confirmed = await confirmC('Are you sure you want to delete this lookup table and all its entries?');
    if (!confirmed) return;
    try {
      await authFetchRequest(`/pricing/lookups/${selectedLookup.id}`, null, 'DELETE');
      setLookups(prev => prev.filter(row => row.id !== selectedLookup.id));
      setSelectedLookup(null);
      setEntries([]);

    } catch (err) {
      console.error('Delete failed:', err);
      alertC('Failed to delete lookup table');
    }
  };

  // Entry CRUD
  const handleSaveEntry = async (formData) => {
    try {
      const payload = { ...formData, pricing_lookup_id: selectedLookup.id };
      let updatedRow;
      if (editEntryData?.id) {
        updatedRow = await authFetchRequest(`/pricing/lookups/${selectedLookup.id}/entries/${editEntryData.id}`, payload, 'PUT');
        setEntries(prev => prev.map(row => row.id === editEntryData.id ? updatedRow : row));
      } else {
        updatedRow = await authFetchRequest(`/pricing/lookups/${selectedLookup.id}/entries`, payload, 'POST');
        setEntries(prev => [...prev, updatedRow]);
      }
      setOpenEntryDialog(false);
      setEditEntryData(null);

    } catch (err) {
      console.error('Save failed:', err);
      alertC('Failed to save entry');
    }
  };

  const handleDeleteEntry = async () => {
    if (!selectedEntry) return;
    const confirmed = await confirmC('Are you sure you want to delete this entry?');
    if (!confirmed) return;
    try {
      await authFetchRequest(`/pricing/lookups/${selectedLookup.id}/entries/${selectedEntry.id}`, null, 'DELETE');
      setEntries(prev => prev.filter(row => row.id !== selectedEntry.id));
      setSelectedEntry(null);

    } catch (err) {
      console.error('Delete failed:', err);
      alertC('Failed to delete entry');
    }
  };

  const lookupColumns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'code', headerName: 'Code', width: 180 },
    { field: 'name', headerName: 'Name', width: 200 },
    { field: 'unit', headerName: 'Unit', width: 120 },
    { field: 'active', headerName: 'Active', width: 80, renderCell: (params) => params.value ? 'Yes' : 'No' }
  ];

  const entryColumns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'key_value', headerName: 'Key', width: 150 },
    { field: 'label', headerName: 'Label', width: 180 },
    { field: 'price', headerName: 'Price', width: 100, renderCell: (params) => `$${parseFloat(params.value).toFixed(2)}` },
    { field: 'sort_order', headerName: 'Sort', width: 80 },
    { field: 'active', headerName: 'Active', width: 80, renderCell: (params) => params.value ? 'Yes' : 'No' }
  ];

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>Lookup Tables (1D)</Typography>

      {/* Lookup Tables Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Lookup Tables</Typography>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <Button variant="contained" size="small" onClick={() => { setEditLookupData(null); setOpenLookupDialog(true); }}>
            Add Table
          </Button>
          <Button variant="contained" color="secondary" size="small" onClick={() => { setEditLookupData(selectedLookup); setOpenLookupDialog(true); }} disabled={!selectedLookup}>
            Edit Table
          </Button>
          <Button variant="outlined" color="error" size="small" onClick={handleDeleteLookup} disabled={!selectedLookup}>
            Delete Table
          </Button>
        </Stack>

        {loading ? <CircularProgress size={24} /> : (
          <DataGrid
            autoHeight
            rowHeight={30}
            rows={lookups}
            columns={lookupColumns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 20, 50]}
            onRowClick={(params) => { setSelectedLookup(params.row); setSelectedEntry(null); }}
            sx={{
              '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#e3f2fd' },
              '& .MuiDataGrid-row': { cursor: 'pointer' }
            }}
            getRowClassName={(params) => params.row.id === selectedLookup?.id ? 'Mui-selected' : ''}
          />
        )}
      </Paper>

      {/* Entries Section */}
      {selectedLookup && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
            Entries for: {selectedLookup.name} ({selectedLookup.code})
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Button variant="contained" size="small" onClick={() => { setEditEntryData(null); setOpenEntryDialog(true); }}>
              Add Entry
            </Button>
            <Button variant="contained" color="secondary" size="small" onClick={() => { setEditEntryData(selectedEntry); setOpenEntryDialog(true); }} disabled={!selectedEntry}>
              Edit Entry
            </Button>
            <Button variant="outlined" color="error" size="small" onClick={handleDeleteEntry} disabled={!selectedEntry}>
              Delete Entry
            </Button>
          </Stack>

          {loadingEntries ? <CircularProgress size={24} /> : (
            <DataGrid
              autoHeight
              rowHeight={30}
              rows={entries}
              columns={entryColumns}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 20, 50]}
              onRowClick={(params) => setSelectedEntry(params.row)}
              sx={{
                '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#e3f2fd' },
                '& .MuiDataGrid-row': { cursor: 'pointer' }
              }}
              getRowClassName={(params) => params.row.id === selectedEntry?.id ? 'Mui-selected' : ''}
            />
          )}
        </Paper>
      )}

      {/* Lookup Table Dialog */}
      <LookupTableDialog
        open={openLookupDialog}
        data={editLookupData}
        onClose={() => { setOpenLookupDialog(false); setEditLookupData(null); }}
        onSave={handleSaveLookup}
      />

      {/* Entry Dialog */}
      <LookupEntryDialog
        open={openEntryDialog}
        data={editEntryData}
        onClose={() => { setOpenEntryDialog(false); setEditEntryData(null); }}
        onSave={handleSaveEntry}
      />
    </Box>
  );
};

const LookupTableDialog = ({ open, data, onClose, onSave }) => {
  const [formData, setFormData] = useState({ code: '', name: '', description: '', unit: '', active: true });

  useEffect(() => {
    if (data) {
      setFormData({
        code: data.code || '',
        name: data.name || '',
        description: data.description || '',
        unit: data.unit || '',
        active: data.active ?? true
      });
    } else {
      setFormData({ code: '', name: '', description: '', unit: '', active: true });
    }
  }, [data, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{data?.id ? 'Edit Lookup Table' : 'Add Lookup Table'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Code" value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value })} fullWidth placeholder="e.g., ref_Textured" />
          <TextField label="Name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} fullWidth placeholder="e.g., Textured Glass Options" />
          <TextField label="Description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} fullWidth multiline rows={2} />
          <TextField label="Unit" value={formData.unit} onChange={(e) => setFormData({ ...formData, unit: e.target.value })} fullWidth placeholder="e.g., per sqft, per linear ft" />
          <FormControlLabel control={<Checkbox checked={formData.active} onChange={(e) => setFormData({ ...formData, active: e.target.checked })} />} label="Active" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(formData)}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

const LookupEntryDialog = ({ open, data, onClose, onSave }) => {
  const [formData, setFormData] = useState({ key_value: '', label: '', price: 0, sort_order: 0, active: true });

  useEffect(() => {
    if (data) {
      setFormData({
        key_value: data.key_value || '',
        label: data.label || '',
        price: data.price || 0,
        sort_order: data.sort_order || 0,
        active: data.active ?? true
      });
    } else {
      setFormData({ key_value: '', label: '', price: 0, sort_order: 0, active: true });
    }
  }, [data, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{data?.id ? 'Edit Entry' : 'Add Entry'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Key" value={formData.key_value} onChange={(e) => setFormData({ ...formData, key_value: e.target.value })} fullWidth placeholder="e.g., Frosted" />
          <TextField label="Label" value={formData.label} onChange={(e) => setFormData({ ...formData, label: e.target.value })} fullWidth placeholder="e.g., Frosted Glass" />
          <TextField label="Price" type="number" value={formData.price} onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })} fullWidth inputProps={{ step: '0.01' }} />
          <TextField label="Sort Order" type="number" value={formData.sort_order} onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })} fullWidth />
          <FormControlLabel control={<Checkbox checked={formData.active} onChange={(e) => setFormData({ ...formData, active: e.target.checked })} />} label="Active" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(formData)}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default LookupTablesTab;
